/**
 * TanStack Query hooks for the System Logs (read-only) surface.
 *
 * Response types are re-exported so the UI layer needs a single import.
 * No mutations — the Logs page is read-only (retention is the only deletion).
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { systemLogKeys } from "@/lib/query";
import type { SystemLogDetail, SystemLogListResponse } from "@/schemas/system-logs";

export type { SystemLogDetail, SystemLogListResponse, SystemLogRow } from "@/schemas/system-logs";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface SystemLogListParams {
  cursor?: string;
  direction?: "next" | "prev";
  level?: string;
  category?: string;
  status?: string;
  method?: string;
  path?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export function useSystemLogs(params: SystemLogListParams = {}) {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.direction) search.set("direction", params.direction);
  if (params.level) search.set("level", params.level);
  if (params.category) search.set("category", params.category);
  if (params.status) search.set("status", params.status);
  if (params.method) search.set("method", params.method);
  if (params.path) search.set("path", params.path);
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.limit) search.set("limit", String(params.limit));
  const suffix = search.toString() ? `?${search.toString()}` : "";

  return useQuery({
    queryKey: systemLogKeys.list(params),
    queryFn: () => jsonFetch<SystemLogListResponse>(`/api/system-logs${suffix}`),
    placeholderData: keepPreviousData,
  });
}

export function useSystemLog(id: string | null, enabled = true) {
  return useQuery({
    queryKey: systemLogKeys.detail(id ?? ""),
    queryFn: () => jsonFetch<SystemLogDetail>(`/api/system-logs/${id}`),
    enabled: enabled && !!id,
  });
}
